import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getStudents, getStaff, executeNewYearWizard } from "../lib/api";
import { useAuth } from "../App";

const STEPS = ["School Year", "Staff", "Students", "Options", "Review"];

// Mirror of backend grade advancement for the preview label
function nextGradeLabel(grade) {
  if (grade == null) return grade;
  const g = String(grade).trim();
  if (!g) return g;
  if (["K", "KG", "KINDER", "KINDERGARTEN"].includes(g.toUpperCase())) return "1";
  const m = g.match(/^(\d+)/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 12) return g;
    const nn = n + 1;
    const suffix = g.slice(m[1].length);
    if (suffix && ["st", "nd", "rd", "th"].includes(suffix.toLowerCase())) {
      const ord = (10 <= nn % 100 && nn % 100 <= 20) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[nn % 10] || "th");
      return `${nn}${ord}`;
    }
    return `${nn}${suffix}`;
  }
  return g;
}

export default function NewYearWizardDialog({ open, onOpenChange, onComplete }) {
  const { isAdmin } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);

  // Data
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);

  // Selections
  const [currentYear, setCurrentYear] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 7 ? `${now.year || now.getFullYear()}-${(now.year || now.getFullYear()) + 1}` : `${(now.year || now.getFullYear()) - 1}-${now.year || now.getFullYear()}`;
  });
  const [newYear, setNewYear] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 7 ? `${(now.year || now.getFullYear()) + 1}-${(now.year || now.getFullYear()) + 2}` : `${now.year || now.getFullYear()}-${(now.year || now.getFullYear()) + 1}`;
  });
  const [staffLeaving, setStaffLeaving] = useState([]);
  const [studentsGraduated, setStudentsGraduated] = useState([]);
  const [studentsLeaving, setStudentsLeaving] = useState([]);
  const [advanceGrade, setAdvanceGrade] = useState(true);
  const [clearSchedule, setClearSchedule] = useState(false);
  const [clearCalendar, setClearCalendar] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [staffRes, studentsRes] = await Promise.all([getStaff(), getStudents("current", true)]);
        setStaff(staffRes.data.filter(s => s.status !== "former").sort((a, b) => a.name.localeCompare(b.name)));
        setStudents(studentsRes.data.sort((a, b) => a.last_name.localeCompare(b.last_name)));
      } catch { toast.error("Failed to load data"); }
      setLoading(false);
    };
    fetchData();
  }, [open]);

  const studentsReturning = useMemo(
    () => students.filter(s => !studentsGraduated.includes(s.id) && !studentsLeaving.includes(s.id)),
    [students, studentsGraduated, studentsLeaving],
  );
  const grade12Students = useMemo(
    () => students.filter(s => s.grade === "12" || s.grade === "12th"),
    [students],
  );
  const staffLeavingNames = useMemo(
    () => staffLeaving.map(id => staff.find(s => s.id === id)?.name).filter(Boolean).join(", "),
    [staffLeaving, staff],
  );

  const handleExecute = async () => {
    if (!window.confirm("Proceed with the New Year transition? A snapshot of the year is saved to Archives and returning students move up a grade. No records are deleted.")) return;
    setExecuting(true);
    try {
      const res = await executeNewYearWizard({
        school_year: currentYear,
        new_school_year: newYear,
        staff_leaving: staffLeaving,
        students_graduated: studentsGraduated,
        students_leaving: studentsLeaving,
        students_returning: studentsReturning.map(s => s.id),
        advance_grade: advanceGrade,
        clear_schedule: clearSchedule,
        clear_calendar: clearCalendar,
      });
      toast.success("New Year Wizard completed! " + res.data.message);
      onComplete?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Wizard failed"); }
    setExecuting(false);
  };

  if (!isAdmin()) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">New Year Wizard</DialogTitle>
          <p className="text-sm text-muted-foreground">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex gap-1 mb-4">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-2 rounded-full ${i <= step ? "bg-[#00305E]" : "bg-gray-200"}`} />
          ))}
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading...</div>
        ) : (
          <>
            {/* Step 0: School Year */}
            {step === 0 && (
              <div className="space-y-4" data-testid="wizard-step-year">
                <p className="text-muted-foreground">Confirm the current school year and the new year starting.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Current Year</Label><Input value={currentYear} onChange={e => setCurrentYear(e.target.value)} data-testid="current-year-input" /></div>
                  <div className="space-y-2"><Label>New Year (starting)</Label><Input value={newYear} onChange={e => setNewYear(e.target.value)} data-testid="new-year-input" /></div>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="font-medium text-blue-800">What happens during the transition:</p>
                  <ul className="mt-2 space-y-1 text-blue-700">
                    <li>A snapshot of the year is saved to Archives — <strong>nothing is deleted</strong>; every attendance and assessment record stays in student files</li>
                    <li>Students you mark as graduated or former become &quot;former&quot;</li>
                    <li>Returning students move up one grade level and keep their full history</li>
                    <li>Staff marked as leaving become &quot;former&quot;</li>
                    <li>You can browse the year snapshot anytime on the Archives page</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 1: Staff */}
            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-step-staff">
                <p className="text-muted-foreground">Select staff members who are <strong>leaving</strong>. Everyone else will carry over to the new year.</p>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0"><tr><th className="p-2 text-left w-10">Leaving?</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Position(s)</th><th className="p-2 text-left">Email</th></tr></thead>
                    <tbody>
                      {staff.map(s => (
                        <tr key={s.id} className={`border-b ${staffLeaving.includes(s.id) ? "bg-red-50" : "hover:bg-muted/50"}`}>
                          <td className="p-2"><Checkbox checked={staffLeaving.includes(s.id)} onCheckedChange={c => setStaffLeaving(c ? [...staffLeaving, s.id] : staffLeaving.filter(id => id !== s.id))} /></td>
                          <td className="p-2 font-medium">{s.name}</td>
                          <td className="p-2 text-xs">{(s.positions || []).join(", ")}</td>
                          <td className="p-2 text-xs text-muted-foreground">{s.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-700 font-medium">{staff.length - staffLeaving.length} returning</span>
                  <span className="text-red-700 font-medium">{staffLeaving.length} leaving</span>
                </div>
              </div>
            )}

            {/* Step 2: Students */}
            {step === 2 && (
              <div className="space-y-4" data-testid="wizard-step-students">
                <p className="text-muted-foreground">Choose what happens to each student. Unmarked students <strong>move up a grade</strong> and stay enrolled. Mark students <strong>Graduated</strong> or <strong>Former</strong> to make them former students.</p>
                {grade12Students.length > 0 && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-800">{grade12Students.length} 12th grade student(s) detected</span>
                      <Button size="sm" variant="outline" className="text-purple-700 border-purple-300" onClick={() => setStudentsGraduated(prev => [...new Set([...prev, ...grade12Students.map(s => s.id)])])}>Mark All 12th Graders Graduated</Button>
                    </div>
                  </div>
                )}
                <div className="border rounded-lg max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0"><tr><th className="p-2 text-left">Name</th><th className="p-2 text-left">Grade</th><th className="p-2 text-left">District</th><th className="p-2 text-center w-24">Graduated</th><th className="p-2 text-center w-24">Former</th></tr></thead>
                    <tbody>
                      {students.map(s => {
                        const isGrad = studentsGraduated.includes(s.id);
                        const isLeave = studentsLeaving.includes(s.id);
                        return (
                          <tr key={s.id} className={`border-b ${isGrad ? "bg-purple-50" : isLeave ? "bg-red-50" : "hover:bg-muted/50"}`}>
                            <td className="p-2 font-medium">{s.first_name} {s.last_name}</td>
                            <td className="p-2 text-xs">{s.grade || "-"}{!isGrad && !isLeave && advanceGrade && <span className="text-green-600"> → {nextGradeLabel(s.grade)}</span>}</td>
                            <td className="p-2 text-xs">{s.school_district || "-"}</td>
                            <td className="p-2 text-center"><Checkbox checked={isGrad} data-testid={`student-grad-${s.id}`} onCheckedChange={c => { setStudentsGraduated(c ? [...studentsGraduated, s.id] : studentsGraduated.filter(id => id !== s.id)); if (c) setStudentsLeaving(studentsLeaving.filter(id => id !== s.id)); }} /></td>
                            <td className="p-2 text-center"><Checkbox checked={isLeave} data-testid={`student-former-${s.id}`} onCheckedChange={c => { setStudentsLeaving(c ? [...studentsLeaving, s.id] : studentsLeaving.filter(id => id !== s.id)); if (c) setStudentsGraduated(studentsGraduated.filter(id => id !== s.id)); }} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-green-50/50">
                  <Checkbox id="advance-grade" checked={advanceGrade} onCheckedChange={setAdvanceGrade} data-testid="advance-grade-checkbox" />
                  <div><Label htmlFor="advance-grade" className="font-medium cursor-pointer">Advance returning students one grade level</Label><p className="text-sm text-muted-foreground mt-1">Each returning student&apos;s grade is bumped by one (e.g., 9 → 10). 12th graders stay at 12.</p></div>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-700 font-medium">{studentsReturning.length} moving up</span>
                  <span className="text-purple-700 font-medium">{studentsGraduated.length} graduated</span>
                  <span className="text-red-700 font-medium">{studentsLeaving.length} former</span>
                </div>
              </div>
            )}

            {/* Step 3: Options */}
            {step === 3 && (
              <div className="space-y-4" data-testid="wizard-step-options">
                <p className="text-muted-foreground">Choose what else to reset for the new year.</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 rounded-lg border">
                    <Checkbox id="clear-schedule" checked={clearSchedule} onCheckedChange={setClearSchedule} />
                    <div><Label htmlFor="clear-schedule" className="font-medium cursor-pointer">Clear Master Schedule</Label><p className="text-sm text-muted-foreground mt-1">Remove all master schedule entries. Block configurations will be preserved.</p></div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg border">
                    <Checkbox id="clear-calendar" checked={clearCalendar} onCheckedChange={setClearCalendar} />
                    <div><Label htmlFor="clear-calendar" className="font-medium cursor-pointer">Clear Calendar Events</Label><p className="text-sm text-muted-foreground mt-1">Remove calendar events (holidays will be kept). You&apos;ll set new dates for the new year.</p></div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <p className="font-medium text-green-800">Always preserved:</p>
                  <ul className="mt-1 space-y-0.5 text-green-700">
                    <li>Contacts, Districts, Block Schedule configurations</li>
                    <li>Student files & documents (for returning students)</li>
                    <li>Staff credentials and permissions</li>
                    <li>All archived data (accessible from Archives page)</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-4" data-testid="wizard-step-review">
                <p className="text-muted-foreground">Review your selections before proceeding.</p>
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="font-semibold text-sm">School Year Transition</p>
                    <p className="text-sm text-muted-foreground">{currentYear} → {newYear}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-semibold text-sm">Staff</p>
                      <p className="text-sm text-green-700">{staff.length - staffLeaving.length} returning</p>
                      {staffLeaving.length > 0 && <p className="text-sm text-red-700">{staffLeaving.length} leaving: {staffLeavingNames}</p>}
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-semibold text-sm">Students</p>
                      <p className="text-sm text-green-700">{studentsReturning.length} moving up a grade{advanceGrade ? "" : " (grade unchanged)"}</p>
                      {studentsGraduated.length > 0 && <p className="text-sm text-purple-700">{studentsGraduated.length} graduating</p>}
                      {studentsLeaving.length > 0 && <p className="text-sm text-red-700">{studentsLeaving.length} former</p>}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="font-semibold text-sm">Data Actions</p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                      <li>Attendance records → Snapshot saved to Archives (kept live)</li>
                      <li>Assessment records → Snapshot saved to Archives (kept live)</li>
                      <li>Incident reports → Snapshot saved to Archives (kept live)</li>
                      {clearSchedule && <li>Master Schedule → Cleared</li>}
                      {clearCalendar && <li>Calendar events → Cleared (holidays kept)</li>}
                    </ul>
                  </div>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold text-green-800">Your data is never deleted</p>
                  <p className="text-sm text-green-700 mt-1">Attendance, assessments, and incidents all stay in student files and reports. A dated snapshot is also saved to the Archives page. Graduated and leaving students are marked as former (their files remain).</p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => step > 0 ? setStep(step - 1) : onOpenChange(false)}>{step === 0 ? "Cancel" : "Back"}</Button>
              {step < STEPS.length - 1 ? (
                <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => setStep(step + 1)} data-testid="wizard-next-btn">Next</Button>
              ) : (
                <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleExecute} disabled={executing} data-testid="wizard-execute-btn">
                  {executing ? "Processing..." : "Execute New Year Transition"}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
